import React, { useMemo, useState } from 'react'
import {
  Paper,
  Box,
  InputAdornment,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material'
import Search from '@mui/icons-material/Search'

import { TextInput } from '../'
import { useIntl } from 'react-intl'

// Funcionalidad básica por defecto
const defaultRenderElement = (element, chosen, keyField, nameField) => (
  <ListItem key={element[keyField]} sx={{ paddingLeft: 2, paddingRight: 2 }}>
    <FormControlLabel
      label={element[nameField]}
      control={<Checkbox checked={chosen} disableRipple />}
      sx={{ margin: 0, width: '100%' }}
    />
  </ListItem>
)

const defaultRenderContainer = ({ children }) => (
  <List sx={{ padding: 0, width: '100%' }}>{children}</List>
)

const defaultFilter = (element, filter, nameField) => {
  return element[nameField].toUpperCase().indexOf(filter) > -1
}

/**
 * @callback RenderElementFunction
 * @param {object} element - El objeto de datos para el elemento actual de la lista.
 * @param {boolean} chosen - Indica si el elemento está actualmente seleccionado.
 * @param {string} keyField - El nombre de la propiedad usada como clave única.
 * @param {string} nameField - El nombre de la propiedad usada como nombre visible.
 * @returns {React.ReactNode} El JSX a renderizar para el elemento de la lista.
 */

/**
 * @callback RenderContainerFunction
 * @param {object} props
 * @param {React.ReactNode} props.children - La lista de elementos renderizados que serán envueltos.
 * @returns {React.ReactNode} El JSX a renderizar como contenedor.
 */

/**
 * @callback SearchFilterFunction
 * @param {object} element - El objeto de datos para el elemento actual de la lista.
 * @param {string} filter - El valor actual del texto de búsqueda.
 * @param {string} nameField - El nombre de la propiedad contra la que se debe filtrar.
 * @returns {boolean} `true` si el elemento debe ser incluido en los resultados, `false` en caso contrario.
 */

/**
 * @typedef {object} CardCheckListProps
 * @property {string} [className] - Nombre de clase CSS para ser aplicado al componente `Paper` raíz.
 * @property {string} [keyField='key'] - El nombre de la propiedad en cada objeto de `elements` que se usará como identificador único.
 * @property {string} [nameField='name'] - El nombre de la propiedad en cada objeto de `elements` que se usará como etiqueta visible.
 * @property {object[]} [elements=[]] - El array de objetos de datos a ser renderizados como la lista de checkboxes.
 * @property {Array<string|number>} [selected=[]] - Un array que contiene las claves (`keyField`) de los elementos actualmente seleccionados.
 * @property {RenderElementFunction} [funcRenderElement=defaultRenderElement] - Una función personalizada para renderizar cada ítem individual en la lista.
 * @property {RenderContainerFunction} [funcRenderContainer=defaultRenderContainer] - Una función personalizada para renderizar el componente que contiene todos los ítems de la lista.
 * @property {boolean} [searchBox] - Si es `true`, se muestra un campo de texto para búsqueda.
 * @property {string} [searchValue] - El valor actual del campo de búsqueda, lo que lo convierte en un componente controlado.
 * @property {string} [searchPlaceholder=''] - Texto de marcador de posición para el campo de búsqueda.
 * @property {React.ElementType} [searchIcon=Search] - El componente de icono a mostrar dentro del campo de búsqueda.
 * @property {SearchFilterFunction} [funcSearchFilter=defaultFilter] - Una función personalizada para filtrar el array `elements` basándose en `searchValue`.
 * @property {function(React.ChangeEvent<HTMLInputElement>): void} [funcSearchOnChange] - La función callback que se ejecuta cuando el valor del campo de búsqueda cambia.
 * @property {boolean} [wholeSelector] - Si es `true`, se muestra una casilla "seleccionar todo" en la parte superior de la lista.
 * @property {string} [wholeCaption='ALL'] - La etiqueta para la casilla "seleccionar todo".
 * @property {function(React.ChangeEvent<HTMLInputElement>, Array<string|number>): void} [funcWholeOnChange] - El callback que se ejecuta cuando la casilla "seleccionar todo" cambia. Recibe el evento y un array con las claves de todos los elementos actualmente visibles.
 * @property {boolean} [toggleCps=false] - Si es `true`, muestra un interruptor que puede ocultar o mostrar el contenido principal de la lista.
 * @property {boolean} [readOnly=false] - Si es `true`, el interruptor `toggleCps` no es interactivo.
 */

/**
 * Un componente de lista de selección (checklist) versátil y altamente personalizable, encapsulado en un `Paper` de Material-UI.
 *
 * Proporciona funcionalidades opcionales como un filtro de búsqueda en tiempo real, una casilla para "seleccionar todo",
 * y un control total sobre el renderizado tanto de los ítems individuales como de su contenedor,
 * lo que lo hace adaptable a una amplia variedad de casos de uso. Opera como un componente controlado,
 * donde el estado de la selección y la búsqueda se gestionan externamente.
 *
 * @component
 * @example
 * const items = [
 * { id: 1, label: 'Manzana' },
 * { id: 2, label: 'Banana' },
 * { id: 3, label: 'Cereza' },
 * { id: 4, label: 'Dátil' },
 * ];
 *
 * const MyChecklistPage = () => {
 * const [selectedItems, setSelectedItems] = useState([2]);
 * const [searchTerm, setSearchTerm] = useState('');
 *
 * const handleSelectAll = (event, visibleKeys) => {
 * if (event.target.checked) {
 * setSelectedItems(visibleKeys);
 * } else {
 * setSelectedItems([]);
 * }
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider>.
 * return (
 * <CardCheckList
 * elements={items}
 * selected={selectedItems}
 * keyField="id"
 * nameField="label"
 * searchBox
 * searchValue={searchTerm}
 * funcSearchOnChange={(e) => setSearchTerm(e.target.value)}
 * searchPlaceholder="Buscar frutas..."
 * wholeSelector
 * wholeCaption="Seleccionar todos los visibles"
 * funcWholeOnChange={handleSelectAll}
 * // Ejemplo de prop de renderizado personalizada
 * funcRenderElement={(element, chosen, keyField, nameField) => (
 * <ListItem key={element[keyField]} dense button onClick={() => {
 * const newSelected = chosen
 * ? selectedItems.filter(id => id !== element[keyField])
 * : [...selectedItems, element[keyField]];
 * setSelectedItems(newSelected);
 * }}>
 * <Checkbox checked={chosen} edge="start" tabIndex={-1} disableRipple />
 * <span style={{ fontWeight: chosen ? 'bold' : 'normal' }}>
 * {element[nameField]}
 * </span>
 * </ListItem>
 * )}
 * />
 * );
 * }
 *
 * @param {CardCheckListProps} props - Las props para el componente CardCheckList.
 */
export const CardCheckList = ({
  className,
  //Main input:
  keyField = 'key',
  nameField = 'name',
  elements = [],
  selected = [],
  funcRenderElement = defaultRenderElement,
  funcRenderContainer = defaultRenderContainer,
  //Optional Search box functionality
  searchBox,
  searchValue,
  searchPlaceholder = '',
  searchIcon = Search,
  funcSearchFilter = defaultFilter,
  funcSearchOnChange = (event) => {},
  //Optional Whole Selection checkbox functionality
  wholeSelector,
  wholeCaption = 'ALL',
  funcWholeOnChange = (event, keys) => {},
  toggleCps = false,
  readOnly = false,
}) => {
  const { formatMessage } = useIntl()

  const [showCps, setShowCps] = useState(selected.length > 0)

  //List to display: we filter element's nameField according to searchValue and render according to the given function
  const [elementsDisplay, elementsChosen, renderedList] = useMemo(() => {
    const elementsChosen = []
    const renderedList = []
    const elementsDisplay = []
    elements.forEach((el) => {
      if (searchValue && !funcSearchFilter(el, searchValue, nameField)) {
        return
      }
      const chosen = selected.indexOf(el[keyField]) > -1
      if (chosen) {
        elementsChosen.push(el)
      }
      renderedList.push(funcRenderElement(el, chosen, keyField, nameField))
      elementsDisplay.push(el[keyField])
      return
    })
    return [elementsDisplay, elementsChosen, renderedList]
  }, [selected, searchValue, elements, nameField, keyField, funcSearchFilter, funcRenderElement])

  //List container component
  const MyContainer = funcRenderContainer

  //Search box:
  let searchBoxJSX = null
  if (searchBox) {
    const SearchIcon = searchIcon
    searchBoxJSX = (
      <Box sx={{ p: 3 }}>
        <TextInput
          name="_search"
          style={{ backgroundColor: 'white' }}
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={funcSearchOnChange}
          InputProps={{
            startAdornment: (
              <InputAdornment sx={{ marginRight: 1 }} position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    )
  }

  //show cps toggle
  let toggleShowCpsJSX = null
  const handleSwitchChange = (event) => {
    const newEvent = { ...event, target: { ...event.target, checked: false }, persist: () => {} }
    if (readOnly) return
    setShowCps((prev) => {
      const newShowCps = !prev
      if (!newShowCps) {
        funcWholeOnChange(newEvent, selected)
      }
      return newShowCps
    })
  }
  if (toggleCps) {
    toggleShowCpsJSX = (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={!showCps}
              onChange={handleSwitchChange}
              readOnly={readOnly}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          }
          label={formatMessage({ id: 'pages.tariffs.zone.zips.toggle' })}
        />
      </Box>
    )
  }

  //Whole selector:
  let wholeSelectorJSX = null
  if (wholeSelector) {
    const wholeNum = elementsDisplay.length
    const wholeChosen = elementsChosen.length
    const isWhole = wholeNum === wholeChosen
    wholeSelectorJSX = (
      <Box sx={{ p: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={isWhole}
              indeterminate={wholeChosen > 0 && !isWhole}
              onChange={(event) => funcWholeOnChange(event, elementsDisplay)}
              disableRipple
            />
          }
          label={wholeCaption + ' (' + wholeNum + ')'}
        />
        <hr />
      </Box>
    )
  }

  //Full component:
  return (
    <Paper elevation={2} className={className}>
      {toggleShowCpsJSX}
      {(showCps || !toggleCps) && (
        <>
          {searchBoxJSX}
          {wholeSelectorJSX}
          <Box
            sx={{
              padding: 2,
              '& .MuiFormControlLabel-root': {
                marginLeft: '0 !important',
              },
              '& > *': {
                paddingLeft: 0,
              },
            }}
          >
            <MyContainer>{renderedList}</MyContainer>
          </Box>
        </>
      )}
    </Paper>
  )
}

export default CardCheckList
